import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import type { Lead } from "@school-sales-ai/shared";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pencil, Trash2, TriangleAlert } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { StagePill } from "@/components/StagePill";
import { ChannelIcon } from "@/components/ChannelIcon";
import { DeletedLeadsBanner } from "@/components/DeletedLeadsBanner";
import { DeleteLeadDialog } from "@/components/DeleteLeadDialog";
import { RestoreLeadDialog } from "@/components/RestoreLeadDialog";
import { useLeads, useDeletedLeads } from "@/hooks/useLeads";
import { useCrmStore } from "@/stores/crm.store";
import { timeAgo } from "@/lib/time-ago";
import { AssigneeBadge } from "@/components/lead-assignment/AssigneeBadge";
import { useLeadAssignmentEnabled } from "@/hooks/useLeadAssignment";
import { CHANNEL_LABEL } from "@/lib/channel";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Desktop components ──────────────────────────────────────

function SkeletonRows({ columns }: { columns: number }): React.JSX.Element {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <TableRow key={i}>
          {Array.from({ length: columns }, (_, j) => j).map((j) => (
            <TableCell key={j}>
              <div className="h-4 rounded bg-ink-100 animate-pulse" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function LeadRow({
  lead,
  showAssignee,
}: {
  lead: Lead;
  showAssignee: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const openEditModal = useCrmStore((s) => s.openEditModal);
  const [deleteOpen, setDeleteOpen] = useState(false);
  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={() => navigate(`/leads/${lead.id}`, { state: { from: "/pipeline" } })}
      >
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-ink-900">
              {lead.name ?? "—"}
            </span>
            <span className="font-mono text-xs text-ink-300">
              {lead.sessionRef}
            </span>
          </div>
        </TableCell>
        <TableCell>
          <span className="text-sm text-ink-700">
            {CHANNEL_LABEL[lead.channelOrigin]}
          </span>
        </TableCell>
        <TableCell>
          <ScoreBadge score={lead.score} size="sm" showLabel />
        </TableCell>
        <TableCell>
          <StagePill stage={lead.stage} small />
        </TableCell>
        {showAssignee && (
          <TableCell className="max-w-[180px]">
            <AssigneeBadge assignedProfileId={lead.assignedProfileId} />
          </TableCell>
        )}
        <TableCell>
          <span className="text-sm text-ink-500">
            {formatDate(lead.lastInteractionAt)}
          </span>
        </TableCell>
        <TableCell>
          {lead.aiPaused ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-300">
              <span className="h-1.5 w-1.5 rounded-full bg-ink-300" />
              Pausado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-wa-green">
              <span className="h-1.5 w-1.5 rounded-full bg-wa-green" />
              Activo
            </span>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openEditModal(lead.id); }}
              className="p-1.5 rounded hover:bg-ink-100 text-ink-400 hover:text-ink-700 transition-colors"
              aria-label="Editar lead"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
              className="p-1.5 rounded hover:bg-red-50 text-ink-400 hover:text-red-600 transition-colors"
              aria-label="Eliminar lead"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </TableCell>
      </TableRow>
      {deleteOpen && (
        <DeleteLeadDialog
          leadId={lead.id}
          leadName={lead.name}
          open
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </>
  );
}

// ─── Mobile card ─────────────────────────────────────────────

function LeadCard({ lead }: { lead: Lead }): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`/leads/${lead.id}`, { state: { from: "/pipeline" } })}
      className="flex items-center gap-3 w-full text-left px-3.5 py-3 bg-white border border-ink-100 rounded-lg shadow-sm cursor-pointer active:bg-ink-050 transition-colors"
    >
      <Avatar name={lead.name ?? lead.sessionRef} size={40} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-[3px]">
          <span className="flex-1 min-w-0 truncate text-[14px] font-semibold text-ink-900">
            {lead.name ?? lead.sessionRef}
          </span>
          <ScoreBadge score={lead.score} size="sm" showLabel />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <StagePill stage={lead.stage} small />
          <ChannelIcon channel={lead.channelOrigin} />
          <span className="text-[10px] text-ink-400 ml-auto">
            {timeAgo(lead.lastInteractionAt)}
          </span>
        </div>
        <AssigneeBadge
          assignedProfileId={lead.assignedProfileId}
          className="mt-1 max-w-full text-[11px]"
        />
      </div>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="oklch(0.72 0.004 90)"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}

// ─── Deleted lead row ────────────────────────────────────────

function formatDeletedAt(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function DeletedLeadRow({ lead }: { lead: Lead }): React.JSX.Element {
  const [restoreOpen, setRestoreOpen] = useState(false);
  return (
    <>
      <TableRow className="bg-red-50">
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm font-medium line-through text-gray-400">
              {lead.name ?? "—"}
            </span>
            <span className="font-mono text-xs text-ink-300">
              {lead.sessionRef}
            </span>
            {lead.deletedAt && (
              <span className="text-xs text-red-400">
                Eliminado el {formatDeletedAt(lead.deletedAt)}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <span className="text-sm text-gray-400">
            {CHANNEL_LABEL[lead.channelOrigin]}
          </span>
        </TableCell>
        <TableCell>
          <ScoreBadge score={lead.score} size="sm" showLabel />
        </TableCell>
        <TableCell>
          <span className="opacity-50">
            <StagePill stage={lead.stage} small />
          </span>
        </TableCell>
        <TableCell />
        <TableCell>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRestoreOpen(true)}
          >
            Restaurar
          </Button>
        </TableCell>
      </TableRow>
      <RestoreLeadDialog
        leadId={lead.id}
        leadName={lead.name}
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
      />
    </>
  );
}

// ─── Deleted mobile card ─────────────────────────────────────

function DeletedLeadCard({ lead }: { lead: Lead }): React.JSX.Element {
  const [restoreOpen, setRestoreOpen] = useState(false);
  return (
    <>
      <div className="flex items-center gap-3 w-full px-3.5 py-3 bg-red-50 border border-red-100 rounded-lg">
        <Avatar name={lead.name ?? lead.sessionRef} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-[3px]">
            <span className="flex-1 min-w-0 truncate text-[14px] font-semibold line-through text-gray-400">
              {lead.name ?? lead.sessionRef}
            </span>
          </div>
          {lead.deletedAt && (
            <span className="text-xs text-red-400">
              Eliminado el {formatDeletedAt(lead.deletedAt)}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRestoreOpen(true)}
        >
          Restaurar
        </Button>
      </div>
      <RestoreLeadDialog
        leadId={lead.id}
        leadName={lead.name}
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
      />
    </>
  );
}

// ─── Deleted-leads switch ────────────────────────────────────

function DeletedToggle({ id }: { id: string }): React.JSX.Element {
  const showDeletedLeads = useCrmStore((s) => s.showDeletedLeads);
  const toggleShowDeletedLeads = useCrmStore((s) => s.toggleShowDeletedLeads);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 border transition-colors duration-200 cursor-pointer ${
              showDeletedLeads
                ? "bg-amber-100 border-amber-400"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            {showDeletedLeads && (
              <TriangleAlert size={14} className="text-amber-600 shrink-0" />
            )}
            <label
              htmlFor={id}
              className={`cursor-pointer select-none text-sm ${
                showDeletedLeads
                  ? "text-amber-800 font-semibold"
                  : "text-amber-700 font-medium"
              }`}
            >
              Mostrar eliminados
            </label>
            <Switch
              id={id}
              checked={showDeletedLeads}
              onCheckedChange={toggleShowDeletedLeads}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Los leads eliminados no aparecen en el tablero</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * The table half of Pipeline de Prospectos: paginated rows on desktop, a card
 * list on mobile, plus the deleted-leads escape hatch.
 *
 * Pagination and "Mostrar eliminados" live here rather than on the page because
 * neither has a meaning on the board — it is unpaginated and has no deleted-card
 * rendering path.
 */
export function TableView(): React.JSX.Element {
  const showDeletedLeads = useCrmStore((s) => s.showDeletedLeads);
  const toggleShowDeletedLeads = useCrmStore((s) => s.toggleShowDeletedLeads);
  const page = useCrmStore((s) => s.page);
  const pageSize = useCrmStore((s) => s.pageSize);
  const setPage = useCrmStore((s) => s.setPage);

  const { data, isLoading, isError } = useLeads({ enabled: !showDeletedLeads });
  const { data: deletedItems, isLoading: deletedLoading } = useDeletedLeads({
    enabled: showDeletedLeads,
  });

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));
  const items = data?.items ?? [];
  // «Asignado» is a column only while the client's assignment policy is on.
  const showAssignee = useLeadAssignmentEnabled();
  const columnCount = showAssignee ? 8 : 7;

  return (
    <>
      {/* ── Mobile ────────────────────────────────── */}
      <div className="md:hidden flex flex-col flex-1 min-h-0">
        <div className="bg-white border-b border-ink-100 px-4 py-2.5 flex items-center justify-end shrink-0">
          <DeletedToggle id="show-deleted-mobile" />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ink-300">
          {showDeletedLeads ? (
            <>
              <DeletedLeadsBanner onDismiss={toggleShowDeletedLeads} />
              {deletedLoading && (
                <div className="flex flex-col gap-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-[76px] rounded-lg bg-red-50 animate-pulse" />
                  ))}
                </div>
              )}
              {!deletedLoading && (deletedItems ?? []).length === 0 && (
                <div className="flex flex-col items-center py-12 gap-2">
                  <span className="text-3xl opacity-30">🗑️</span>
                  <span className="font-display font-bold text-[18px] text-ink-400">
                    No hay leads eliminados
                  </span>
                </div>
              )}
              {!deletedLoading &&
                (deletedItems ?? []).map((lead) => (
                  <DeletedLeadCard key={lead.id} lead={lead} />
                ))}
            </>
          ) : (
            <>
              {isLoading && (
                <div className="flex flex-col gap-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-[76px] rounded-lg bg-ink-100 animate-pulse" />
                  ))}
                </div>
              )}
              {isError && (
                <p className="text-center text-sm text-red-500 py-12">
                  Error al cargar prospectos.
                </p>
              )}
              {!isLoading && !isError && items.map((lead) => (
                <LeadCard key={lead.id} lead={lead} />
              ))}
              {!isLoading && !isError && items.length === 0 && (
                <div className="flex flex-col items-center py-12 gap-2">
                  <span className="text-3xl opacity-30">🔍</span>
                  <span className="font-display font-bold text-[18px] text-ink-400">
                    Sin resultados
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Desktop ───────────────────────────────── */}
      <div className="hidden md:flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
        <div className="flex justify-end">
          <DeletedToggle id="show-deleted" />
        </div>

        {showDeletedLeads ? (
          <>
            <DeletedLeadsBanner onDismiss={toggleShowDeletedLeads} />
            <div className="rounded-lg border border-ink-100 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Etapa</TableHead>
                    <TableHead />
                    <TableHead>Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deletedLoading && <SkeletonRows columns={6} />}
                  {!deletedLoading && (deletedItems ?? []).length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-sm text-ink-300 py-8"
                      >
                        No hay leads eliminados.
                      </TableCell>
                    </TableRow>
                  )}
                  {!deletedLoading &&
                    (deletedItems ?? []).map((lead) => (
                      <DeletedLeadRow key={lead.id} lead={lead} />
                    ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-ink-100 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Etapa</TableHead>
                    {showAssignee && <TableHead>Asignado</TableHead>}
                    <TableHead>Última interacción</TableHead>
                    <TableHead>IA</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <SkeletonRows columns={columnCount} />}
                  {isError && (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        className="text-center text-sm text-red-500 py-8"
                      >
                        Error al cargar prospectos. Intenta de nuevo.
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && !isError && items.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        className="text-center text-sm text-ink-300 py-8"
                      >
                        No se encontraron prospectos.
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading &&
                    !isError &&
                    items.map((lead) => (
                      <LeadRow key={lead.id} lead={lead} showAssignee={showAssignee} />
                    ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-ink-300">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1 || isLoading}
                >
                  ← Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages || isLoading}
                >
                  Siguiente →
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
